import React, { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import { toastPermissionDenied } from '../utils/permissionToast';
import { useAuth } from '../contexts/AuthContext';
import EmployeeForm from '../components/EmployeeForm';

const AddEmployee = () => {
  const { hasPermission } = useAuth();
  const methods = useForm({
    defaultValues: { taxable: true }
  });
  const { handleSubmit, formState: { isSubmitting } } = methods;
  const navigate = useNavigate();
  
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [banks, setBanks] = useState([]);
  const [backendError, setBackendError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!hasPermission('employees', 'create')) {
      toastPermissionDenied('You do not have permission to add employees');
      navigate('/employees');
      return;
    }
    
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [deptRes, empRes, pmRes, bankRes] = await Promise.all([
          api.get('/api/departments'),
          api.get('/api/employees', { params: { status: 'active', limit: 2000 } }),
          api.get('/api/payment-methods'),
          api.get('/api/banks')
        ]);
        
        setDepartments(deptRes.data.data);
        setEmployees(empRes.data.data || []);
        setPaymentMethods(pmRes.data.data);
        setBanks(bankRes.data.data);
      } catch (error) {
        toast.error('Failed to load data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [hasPermission, navigate]);

  const onSubmit = async (data) => {
    setBackendError('');
    try {
      await api.post('/api/employees', data);
      toast.success('Employee added successfully!');
      navigate('/employees');
    } catch (error) {
      setBackendError(error.response?.data?.message || 'Failed to add employee');
      toast.error(error.response?.data?.message || 'Failed to add employee');
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto bg-white p-10 rounded-lg shadow-soft">
        <div className="text-center">Loading form data...</div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto bg-white p-10 rounded-lg shadow-soft">
      <Link
        to="/employees"
        data-testid="employee-form-cancel"
        className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Employees
      </Link>
      <h2 className="text-2xl font-bold mb-8">Add New Employee</h2>
      {backendError && <div className="mb-6 text-red-600 text-sm font-medium">{backendError}</div>}
      
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <EmployeeForm
            departments={departments}
            employees={employees}
            paymentMethods={paymentMethods}
            banks={banks}
            mode="add"
            showSalarySection={true}
          />
          <div className="pt-4">
            <button type="submit" disabled={isSubmitting} data-testid="employee-form-submit" className="w-full bg-primary-600 text-white py-3 px-6 rounded-md hover:bg-primary-700 transition-colors font-medium">
              {isSubmitting ? 'Adding...' : 'Add Employee'}
            </button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default AddEmployee; 